import AccountBoxIcon from "@mui/icons-material/AccountBox";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SchoolIcon from "@mui/icons-material/School";
import SettingsIcon from "@mui/icons-material/Settings";
import { CircularProgress } from "@mui/material";
import isDarkColor from "is-dark-color";
import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import "../assets/style/components/Navbar.scss";
import { PAGES_ENDPOINTS } from "../constants/Endpoints";
import { AUTHORIZED_SETTINGS_USER_ROLES } from "../constants/Roles";
import { getSettings } from "../pages/Settings";
import { useUserInfo } from "../providers/UserProvider";
import ProfileDialog from "./dialog/ProfileDialog";

/**
 * This function is responsible for calculating the accent color of a given color
 * @param {String} color Hex color including the leading #
 * @param {Number} amount
 * @returns The accent color of the given color
 */
function getColorAccent(color, amount) {
  return (
    "#" +
    color
      .replace(/^#/, "")
      .replace(/../g, (color) =>
        (
          "0" +
          Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)
        ).substr(-2)
      )
  );
}

/**
 * This function returns a styled sidebar as a React component.
 * @returns Sidebar as React component
 */
export default function Navbar({ themeColor, setThemeColor, children }) {
  const { isAuthenticated, userInfo } = useUserInfo();
  const [hoveredItem, setHoveredItem] = useState();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const menuItems = useMemo(() => {
    if (!userInfo) return [];
    return [
      {
        path: PAGES_ENDPOINTS.MAIN,
        name: "Leerlingen",
        icon: <AccountBoxIcon />,
      },
      {
        path: PAGES_ENDPOINTS.SUBJECTS,
        name: "Vakken",
        icon: <CollectionsBookmarkIcon />,
      },
      {
        path: PAGES_ENDPOINTS.TEACHERS,
        name: "Docenten",
        icon: <SchoolIcon />,
      },
      userInfo.user.role in AUTHORIZED_SETTINGS_USER_ROLES
        ? {
            path: PAGES_ENDPOINTS.SETTINGS,
            name: "Instellingen",
            icon: <SettingsIcon />,
          }
        : null,
      {
        path: PAGES_ENDPOINTS.PROFILE,
        name: "Profiel",
        icon: <ManageAccountsIcon />,
        bottom: true,
      },
    ];
  }, [userInfo]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      getSettings().then((settings) => {
        setThemeColor(settings.themeColor);
        setLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const excludedColors = {
    override: {
      "#EE7A65": true,
    },
  };

  const colorAccent = getColorAccent(
    themeColor,
    isDarkColor(themeColor, excludedColors) ? 50 : -50
  );

  const showPopup =
    isAuthenticated &&
    location.pathname !== PAGES_ENDPOINTS.PROFILE &&
    (userInfo.user.firstName == null || userInfo.user.lastName == null);

  return (
    <div className="container">
      {isAuthenticated && loading && (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "100vw",
            }}
          >
            <CircularProgress />
          </div>
        </div>
      )}
      {isAuthenticated && !loading && (
        <div
          className="navbar"
          style={{
            backgroundColor: themeColor,
          }}
        >
          {menuItems
            .filter((x) => x !== null)
            .map((item, i) => (
              <NavLink
                to={item.path}
                key={i}
                className={`link ${item.bottom ? "bottom" : null}`}
                onClick={(event) => {
                  if (
                    userInfo.user.firstName == null ||
                    userInfo.user.lastName == null
                  ) {
                    event.preventDefault();
                  }
                }}
                style={{
                  color: isDarkColor(themeColor, excludedColors)
                    ? "white"
                    : "black",
                  backgroundColor:
                    item.path === location.pathname ||
                    hoveredItem?.path === item.path
                      ? colorAccent
                      : themeColor,
                  cursor:
                    userInfo.user.firstName == null ||
                    userInfo.user.lastName == null
                      ? "not-allowed"
                      : "pointer",
                }}
                onMouseEnter={() => {
                  setHoveredItem(item);
                }}
                onMouseLeave={() => {
                  setHoveredItem(null);
                }}
              >
                <div className="icon">{item.icon}</div>
                <div className="link_text">{item.name}</div>
              </NavLink>
            ))}
          <div className="logo">
            <img src="/logo192.png" alt="logo" />
          </div>
        </div>
      )}

      {isAuthenticated && (
        <ProfileDialog open={showPopup} user={userInfo.user} />
      )}

      <main>{!loading && children}</main>
    </div>
  );
}
